import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import { acquireTokenSilent } from '@/lib/msal';
import { protectedResources } from '@/lib/msalConfig';
import {
  ICE_SERVER_WAIT_TIMEOUT,
  MIC_CAPTURE_MAX_RETRIES,
  MIC_CAPTURE_RETRY_DELAY_MS,
  RECONNECT_MAX_RETRIES,
} from '../constants';
import type { ConnectionState, VoiceLiveServerMessage } from '../types';
import { buildVoiceLiveWsUrl, calculateReconnectDelay } from '../utils';
import { useBeforeUnload } from './useBeforeUnload';
import { useNetworkStatus } from './useNetworkStatus';
import type { UseAvatarFallbackResult } from './useAvatarFallback';
import type { UseAvatarPeerConnectionResult } from './useAvatarPeerConnection';
import type { UseConversationTranscriptResult } from './useConversationTranscript';
import type { UseTokenUsageResult } from './useTokenUsage';
import type { UseVoiceLiveAudioResult } from './useVoiceLiveAudio';
import type { UseVoiceLiveTelemetryResult } from './useVoiceLiveTelemetry';

export interface UseVoiceLiveConnectionOptions {
  scenarioId: string;
  userId: string;
  onSessionCreated?: (sessionId: number) => void;
  // Shared refs owned by the composition root (also used by peer/fallback).
  wsRef: MutableRefObject<WebSocket | null>;
  fieldOneSessionIdRef: MutableRefObject<number | null>;
  isVideoEnabledRef: MutableRefObject<boolean>;
  autoStartAvatarRef: MutableRefObject<boolean>;
  iceServerTimeoutRef: MutableRefObject<number | null>;
  hasIceServersRef: MutableRefObject<boolean>;
  iceServersRef: MutableRefObject<RTCIceServer[]>;
  shouldReconnectRef: MutableRefObject<boolean>;
  savedUserIdRef: MutableRefObject<string>;
  // Session setters.
  setErrorMessage: (message: string) => void;
  setAvatarIceServers: (servers: RTCIceServer[]) => void;
  // Composed sub-hooks.
  telemetry: UseVoiceLiveTelemetryResult;
  transcriptStore: UseConversationTranscriptResult;
  tokenUsage: UseTokenUsageResult;
  audio: UseVoiceLiveAudioResult;
  peer: UseAvatarPeerConnectionResult;
  fallback: UseAvatarFallbackResult;
}

export interface UseVoiceLiveConnectionResult {
  /** WebSocket session lifecycle state. */
  connectionState: ConnectionState;
  /** Whether the session is active (session.updated received). */
  isActive: boolean;
  /** Current reconnect attempt count (for the UI). */
  retryCount: number;
  /** Start (or reconnect) the session. */
  handleStart: (userId: string, isReconnect?: boolean) => Promise<void>;
  /** Stop the session (user-initiated) and reset all state. */
  handleStop: () => void;
  /** Retry after a failure, reusing the FieldOne session if present. */
  handleManualRetry: () => void;
}

/**
 * Owns the WebSocket session lifecycle: connect/auth, reconnection with
 * exponential backoff, network online/offline handling, the server-message
 * dispatch, and clean teardown. It drives the avatar peer, audio, and fallback
 * subsystems through the injected hook results.
 */
export const useVoiceLiveConnection = ({
  scenarioId,
  userId,
  onSessionCreated,
  wsRef,
  fieldOneSessionIdRef,
  isVideoEnabledRef,
  autoStartAvatarRef,
  iceServerTimeoutRef,
  hasIceServersRef,
  iceServersRef,
  shouldReconnectRef,
  savedUserIdRef,
  setErrorMessage,
  setAvatarIceServers,
  telemetry,
  transcriptStore,
  tokenUsage,
  audio,
  peer,
  fallback,
}: UseVoiceLiveConnectionOptions): UseVoiceLiveConnectionResult => {
  // Re-destructure the sub-hooks to the local names the logic below expects.
  const {
    createContext: createAudioContext,
    closeContext: closeAudioContext,
    startCapture: startAudioCapture,
    stopCapture: stopAudioCapture,
    teardownAnalysers: teardownAudioAnalysers,
    playDelta: playFallbackAudioDelta,
    stopPlayback: stopFallbackAudioPlayback,
  } = audio;
  const { avatarState, avatarStateRef, setAvatarState, startAvatar, cleanupAvatar } = peer;
  const {
    isAudioFallbackRef,
    avatarEverActiveRef,
    failAvatarEstablishment,
    reset: resetFallback,
    cancelPeerRetry,
  } = fallback;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [isActive, setIsActive] = useState(false);
  const [retryCount, setRetryCount] = useState<number>(0);

  // Reconnection / dispatch refs (avoid stale closures in WS callbacks).
  const reconnectTimeoutRef = useRef<number | null>(null);
  const isManualDisconnectRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0);
  const isReconnectingRef = useRef<boolean>(false);
  const sessionReadyRef = useRef<boolean>(false);

  // Attempt to reconnect (uses refs to avoid stale closures)
  const attemptReconnect = () => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
    }

    // Guard: already reconnecting, disabled, or max retries reached
    if (isReconnectingRef.current) {
      console.log('Reconnection already in progress, skipping duplicate attempt');
      return;
    }

    if (!shouldReconnectRef.current || retryCountRef.current >= RECONNECT_MAX_RETRIES) {
      console.log(
        `Max retries reached (${retryCountRef.current}/${RECONNECT_MAX_RETRIES}) or reconnection disabled`,
      );
      isReconnectingRef.current = false;
      setConnectionState('disconnected');
      setErrorMessage('Connection failed. Please try again manually.');
      return;
    }

    isReconnectingRef.current = true;
    const currentRetry = retryCountRef.current;
    const delay = calculateReconnectDelay(currentRetry);
    console.log(
      `Reconnecting in ${Math.round(delay / 1000)}s (attempt ${currentRetry + 1}/${RECONNECT_MAX_RETRIES})...`,
    );
    setConnectionState('reconnecting');
    telemetry.track('WebRTC:ReconnectScheduled', {
      attempt: currentRetry + 1,
      maxRetries: RECONNECT_MAX_RETRIES,
      delayMs: Math.round(delay),
      userId: savedUserIdRef.current,
    });

    reconnectTimeoutRef.current = window.setTimeout(() => {
      retryCountRef.current += 1;
      setRetryCount(retryCountRef.current);
      handleStart(savedUserIdRef.current, true);
    }, delay);
  };

  /**
   * Send session.finalize with the full transcript to the backend so
   * it can POST the transcript before the WS closes.
   */
  const sendFinalizeMessage = () => {
    if (
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      transcriptStore.hasTurns()
    ) {
      wsRef.current.send(JSON.stringify(transcriptStore.buildFinalizePayload()));
      console.log(`Sent session.finalize with ${transcriptStore.turnCount()} turns`);
    }
  };

  const cleanup = () => {
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear any pending avatar peer-retry timeout
    cancelPeerRetry();

    // Stop any in-flight audio-only fallback playback
    stopFallbackAudioPlayback();

    // Tear down the input/output meter analysers before the context closes.
    teardownAudioAnalysers();

    // Stop audio capture (mic stream + worklet)
    stopAudioCapture();

    // Send session.finalize with full transcript before closing the WS
    // so the backend can POST the transcript on this clean disconnect.
    sendFinalizeMessage();

    // Close WebSocket with code 4000 (user-initiated) so the backend
    // can finalize the session immediately instead of waiting for reconnect
    if (wsRef.current) {
      wsRef.current.close(4000, 'User initiated disconnect');
      wsRef.current = null;
    }

    // Close audio context
    closeAudioContext();

    // Cleanup avatar
    cleanupAvatar();
  };

  const handleStart = async (userId: string, isReconnect: boolean = false) => {
    try {
      // Release the reconnection lock — the scheduled attempt has been consumed.
      // This allows ws.onclose (if this attempt also fails) to start the next cycle.
      isReconnectingRef.current = false;

      // Capture connect start time for phase timing telemetry
      telemetry.markConnectStart();

      // Save connection parameters for reconnection
      savedUserIdRef.current = userId;

      if (!isReconnect) {
        // Reset retry count on manual start
        retryCountRef.current = 0;
        setRetryCount(0);
        isManualDisconnectRef.current = false;
        shouldReconnectRef.current = true;
      }

      setConnectionState(isReconnect ? 'reconnecting' : 'connecting');
      setErrorMessage('');
      // Use WebSocket audio mode (no WebRTC avatar) when either the network
      // forced a fallback OR the user explicitly chose audio-only. The ref check
      // covers forced fallback and defaultVideoEnabled=false; the video-enabled
      // ref covers the case where the user toggled to audio before starting.
      const shouldUseAudioMode = isAudioFallbackRef.current || !isVideoEnabledRef.current;
      autoStartAvatarRef.current = !shouldUseAudioMode;
      // Sync isAudioFallbackRef to the resolved mode so that:
      //  • response.audio.delta messages are played (handler guards on this ref)
      //  • failAvatarEstablishment short-circuits in audio mode
      //  • the “Video on” switch correctly calls reattemptAvatarFromFallback
      // This matters when audio mode was entered via the user toggle rather
      // than a network failure (enterAudioFallback sets it but the toggle path
      // previously left it false).
      if (shouldUseAudioMode) {
        isAudioFallbackRef.current = true;
      }
      sessionReadyRef.current = false;
      hasIceServersRef.current = false;
      iceServersRef.current = [];

      // Clear any previous ICE server timeout to prevent false-positives
      // from overlapping handleStart calls
      if (iceServerTimeoutRef.current) {
        window.clearTimeout(iceServerTimeoutRef.current);
        iceServerTimeoutRef.current = null;
      }

      // Close old WebSocket before creating a new one to prevent orphaned
      // connections whose onclose would fork another reconnection chain
      if (wsRef.current) {
        const oldWs = wsRef.current;
        // Detach handlers so the old WS close doesn't trigger reconnect, and so
        // a not-yet-open socket's onopen can't start mic capture against the
        // audio context we're about to close and replace below.
        oldWs.onclose = null;
        oldWs.onerror = null;
        oldWs.onmessage = null;
        oldWs.onopen = null;
        oldWs.close();
        wsRef.current = null;
      }

      // Reset audio meter analysers tied to the old context.
      teardownAudioAnalysers();

      // Close old audio context before creating a new one
      closeAudioContext();

      // Stop old audio capture (mic stream + worklet)
      stopAudioCapture();

      // On reconnect, tear down the old avatar/WebRTC so startAvatar()
      // can create a fresh peer connection with the new ICE servers
      if (isReconnect) {
        cleanupAvatar();
        setAvatarState('inactive');
      }

      // Set timeout for ICE servers
      iceServerTimeoutRef.current = window.setTimeout(() => {
        if (autoStartAvatarRef.current && !hasIceServersRef.current) {
          telemetry.track('WebRTC:IceServerTimeout', {
            userId: savedUserIdRef.current,
            error: 'Could not initialize avatar. Please try again.',
          });
          autoStartAvatarRef.current = false;
          if (!avatarEverActiveRef.current) {
            // Backend never delivered ICE servers — avatar can't be set up.
            // Fall back to audio-only over WebSocket.
            failAvatarEstablishment('ice-server-timeout');
          } else {
            setErrorMessage('Could not initialize avatar. Please try again.');
          }
        }
      }, ICE_SERVER_WAIT_TIMEOUT);

      // Create audio context with 24kHz sample rate
      createAudioContext();

      // Connect to backend WebSocket
      // Resolve backend URL (supports Azure runtime override)
      const backendUrl = process.env.NEXT_PUBLIC_VITE_BACKEND_URL || `http://localhost:8000`;
      console.log('Backend URL for WebSocket:', backendUrl);
      // Request an audio-only session (mode=audio) from the backend when in
      // fallback mode or when the user chose audio-only, so Azure streams the
      // response audio over the WebSocket (no avatar/WebRTC). session_id is
      // included only on reconnect so the backend reuses the FieldOne session.
      const { wsUrl, host } = buildVoiceLiveWsUrl({
        backendUrl,
        userId,
        scenarioId,
        sessionId: fieldOneSessionIdRef.current,
        isReconnect,
        audioMode: shouldUseAudioMode,
      });
      telemetry.setBackendHost(host);
      const nav = navigator as Navigator & {
        connection?: { effectiveType?: string; downlink?: number; rtt?: number };
      };
      telemetry.track('WebRTC:ConnectStart', {
        isReconnect,
        effectiveType: nav.connection?.effectiveType,
        rttMs: nav.connection?.rtt,
        downlinkMbps: nav.connection?.downlink,
        userId,
      });
      console.log('Connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log('WebSocket connected');
        telemetry.track('WebRTC:WsConnected', {
          elapsedMs: telemetry.elapsedMs(),
          isReconnect,
          userId,
        });

        // Send authentication message as first message
        try {
          const token = await acquireTokenSilent(protectedResources.backendApi.scopes);
          if (token) {
            ws.send(
              JSON.stringify({
                type: 'auth',
                token: token,
              }),
            );
            console.log('Authentication message sent to WebSocket');
          } else {
            console.error('Failed to acquire authentication token');
            ws.close(4000, 'Failed to acquire auth token');
            return;
          }
        } catch (error) {
          console.error('Error acquiring token for WebSocket auth:', error);
          ws.close(4000, 'Auth token acquisition failed');
          return;
        }

        // A reconnect / manual retry / stop can supersede this socket while we
        // await the auth token above. If so, wsRef now points elsewhere and the
        // audio context this handler would use has been closed/replaced — bail
        // before starting mic capture to avoid a use-after-close race.
        if (wsRef.current !== ws) {
          console.log('WebSocket superseded during auth handshake; skipping session start');
          return;
        }

        setConnectionState('connected');

        // Set session start time on first successful connection
        transcriptStore.ensureStartTime();

        // Clear reconnection lock and reset retry count on successful connection
        isReconnectingRef.current = false;
        if (retryCountRef.current > 0) {
          console.log('Reconnection successful');
          telemetry.track('WebRTC:ReconnectSucceeded', {
            attempt: retryCountRef.current,
            userId,
          });
          retryCountRef.current = 0;
          setRetryCount(0);
        }

        // On reconnect, send accumulated transcript to the (possibly new) backend pod
        if (isReconnect && transcriptStore.hasTurns()) {
          ws.send(JSON.stringify(transcriptStore.buildRestorePayload()));
          console.log(`Sent session.restore_transcript with ${transcriptStore.turnCount()} turns`);
        }

        // Start microphone capture after WebSocket is ready. On reconnect the
        // previous connection's mic stream may not have fully released at the
        // OS level yet, so retry a couple of times before treating it as fatal.
        const startCaptureWithRetry = async () => {
          for (let attempt = 0; attempt <= MIC_CAPTURE_MAX_RETRIES; attempt++) {
            try {
              await startAudioCapture((base64Audio) => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                  wsRef.current.send(
                    JSON.stringify({
                      type: 'input_audio_buffer.append',
                      audio: base64Audio,
                    }),
                  );
                }
              });
              return;
            } catch (error) {
              if (attempt < MIC_CAPTURE_MAX_RETRIES) {
                console.warn(
                  `Audio capture attempt ${attempt + 1} failed, retrying in ${MIC_CAPTURE_RETRY_DELAY_MS}ms:`,
                  error,
                );
                telemetry.track('WebRTC:MicCaptureRetry', {
                  attempt: attempt + 1,
                  userId,
                });
                await new Promise((resolve) =>
                  window.setTimeout(resolve, MIC_CAPTURE_RETRY_DELAY_MS),
                );
                continue;
              }
              console.error('Failed to start audio capture:', error);
              const detail =
                error instanceof DOMException || error instanceof Error
                  ? error.message
                  : String(error);
              const errorName =
                error instanceof DOMException || error instanceof Error ? error.name : 'Unknown';
              // Only a genuine getUserMedia permission/hardware rejection should
              // block with an alert; the teardown race now aborts quietly in
              // startCapture and never reaches here. Anything else (e.g. the
              // worklet asset failing to load) is surfaced in-page instead.
              const isPermissionError =
                error instanceof DOMException &&
                [
                  'NotAllowedError',
                  'NotFoundError',
                  'NotReadableError',
                  'SecurityError',
                  'OverconstrainedError',
                ].includes(error.name);
              telemetry.track('WebRTC:MicCaptureFailed', { errorName, userId });
              if (isPermissionError) {
                alert(
                  `Failed to access microphone: ${detail}\n\nPlease:\n1. Check that this site has microphone permission in your browser\n2. Make sure a microphone is connected and not in use by another app\n3. Allow microphone access when prompted, then try again`,
                );
              } else {
                setErrorMessage(`Could not start the microphone: ${detail}. Please try again.`);
              }
              handleStop();
            }
          }
        };
        await startCaptureWithRetry();
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        handleServerMessage(message);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Don't set error message or stop immediately - let onclose handle reconnection
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed', event.code, event.reason);
        telemetry.track('WebRTC:WsClosed', {
          closeCode: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          userId: savedUserIdRef.current,
        });

        // Determine if we should attempt reconnection
        const shouldAttemptReconnect =
          shouldReconnectRef.current &&
          !isManualDisconnectRef.current &&
          event.code !== 1000 && // Normal closure
          event.code !== 1001 && // Going away
          event.code !== 4000 && // Custom: User initiated
          event.code !== 4001 && // Custom: Auth failure
          event.code !== 4003; // Custom: Forbidden

        if (shouldAttemptReconnect && retryCountRef.current < RECONNECT_MAX_RETRIES) {
          console.log('Connection lost, attempting to reconnect...');
          // Don't set connectionState='disconnected' here — attemptReconnect
          // will set 'reconnecting', avoiding a flash of the Start button
          attemptReconnect();
        } else {
          // Final disconnect — NOT calling handleStop() because it would
          // clear errorMessage before we can set the appropriate one below.
          isReconnectingRef.current = false;
          setConnectionState('disconnected');
          telemetry.track('WebRTC:FinalDisconnect', {
            closeCode: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            retryCount: retryCountRef.current,
            userId: savedUserIdRef.current,
          });

          // Set appropriate error message
          if (retryCountRef.current >= RECONNECT_MAX_RETRIES) {
            setErrorMessage(
              'Unable to maintain connection. Please check your internet and try again.',
            );
          } else if (event.code === 4001) {
            setErrorMessage('Authentication failed. Please refresh and try again.');
          } else if (!shouldReconnectRef.current) {
            // Normal user-initiated disconnect, no error
          } else {
            setErrorMessage('Connection closed. Please try again.');
          }
        }
      };
    } catch (error) {
      console.error('Failed to start:', error);
      setErrorMessage('Connection failed. Please check your internet connection.');
      autoStartAvatarRef.current = false;
      if (iceServerTimeoutRef.current) {
        window.clearTimeout(iceServerTimeoutRef.current);
      }
      cleanup();
      setConnectionState('disconnected');
    }
  };

  const checkAndAutoStartAvatar = (
    hasIceServers: boolean,
    isSessionReady: boolean,
    iceServers?: RTCIceServer[],
  ) => {
    console.log('Checking auto-start conditions:', {
      autoStart: autoStartAvatarRef.current,
      sessionReady: isSessionReady,
      hasIceServers,
      avatarState,
      iceServersProvided: iceServers?.length || 0,
    });

    if (
      autoStartAvatarRef.current &&
      isSessionReady &&
      hasIceServers &&
      avatarStateRef.current === 'inactive'
    ) {
      console.log('Auto-starting avatar...');
      autoStartAvatarRef.current = false;
      if (iceServerTimeoutRef.current) {
        window.clearTimeout(iceServerTimeoutRef.current);
      }
      startAvatar(iceServers);
    }
  };

  const handleServerMessage = (message: VoiceLiveServerMessage) => {
    console.log('Received message:', message.type);

    switch (message.type) {
      case 'session.updated':
        console.log('Session ready');
        setConnectionState('ready');
        setIsActive(true);
        sessionReadyRef.current = true;
        telemetry.track('WebRTC:SessionReady', {
          elapsedMs: telemetry.elapsedMs(),
          userId,
        });
        checkAndAutoStartAvatar(hasIceServersRef.current, true, iceServersRef.current);
        break;

      case 'fieldone_session.created':
        if (message.session_id) {
          console.log('FieldOne session created:', message.session_id);
          fieldOneSessionIdRef.current = message.session_id;
          onSessionCreated?.(message.session_id);
        }
        break;

      case 'ice_servers':
        // ICE servers received from Azure
        if (message.servers && message.servers.length > 0) {
          console.log(`Received ${message.servers.length} ICE servers`);
          setAvatarIceServers(message.servers);
          iceServersRef.current = message.servers;
          hasIceServersRef.current = true;
          telemetry.setIceServerUrls(message.servers);
          telemetry.track('WebRTC:IceServersReceived', {
            serverCount: message.servers.length,
            serverUrls: telemetry.iceServerUrls(),
            elapsedMs: telemetry.elapsedMs(),
            userId,
          });
          if (iceServerTimeoutRef.current) {
            window.clearTimeout(iceServerTimeoutRef.current);
          }
          checkAndAutoStartAvatar(true, sessionReadyRef.current, message.servers);
        }
        break;

      case 'avatar_answer':
        // Avatar SDP answer received - handled in startAvatar
        console.log('Avatar SDP answer received');
        break;

      case 'avatar_error':
        console.error('Avatar error:', message.error?.message);
        setAvatarState('failed');
        if (!avatarEverActiveRef.current) {
          // Initial avatar failure reported by the backend — retry or fall back.
          failAvatarEstablishment('avatar_error');
        } else {
          setErrorMessage('Avatar video connection failed. Please try again.');
        }
        break;

      case 'input_audio_buffer.speech_started':
        console.log('User started speaking');
        break;

      case 'input_audio_buffer.speech_stopped':
        console.log('User stopped speaking');
        break;

      case 'response.created':
        console.log('Assistant response created');
        break;

      case 'response.audio.delta':
        // In audio-only fallback the assistant audio arrives here over the
        // WebSocket (base64 PCM16) and is played via Web Audio. In WebRTC mode
        // the avatar media track carries the audio, so these are ignored.
        if (isAudioFallbackRef.current && message.delta) {
          playFallbackAudioDelta(message.delta);
        }
        break;

      case 'response.audio.done':
        console.log('Assistant finished speaking');
        break;

      case 'conversation.item.truncated': {
        // Barge-in: stop any in-flight fallback audio so the assistant goes
        // silent immediately when the user interrupts.
        if (isAudioFallbackRef.current) {
          stopFallbackAudioPlayback();
        }
        transcriptStore.markLastAssistantInterrupted();
        break;
      }

      case 'response.done':
        console.log('Response complete');
        if (message.token_usage) {
          // DEV token readout: fold this response's usage into the running total
          tokenUsage.record(message.token_usage);
        }
        if (message.transcript) {
          // Append to frontend-owned canonical transcript
          transcriptStore.appendTurn('assistant', message.transcript!);
        }
        break;

      case 'conversation.item.created':
        if (message.transcript) {
          console.log('User transcript:', message.transcript);
          // Append to frontend-owned canonical transcript
          transcriptStore.appendTurn('user', message.transcript!);
        }
        break;

      case 'error':
        console.error('Server error:', message.error?.message);
        break;
    }
  };

  const handleStop = () => {
    setIsActive(false);
    isManualDisconnectRef.current = true;
    shouldReconnectRef.current = false;
    isReconnectingRef.current = false;
    autoStartAvatarRef.current = false;
    sessionReadyRef.current = false;
    hasIceServersRef.current = false;
    iceServersRef.current = [];
    // Reset audio-only fallback state (cleanup() stops playback + peer-retry).
    resetFallback();
    if (iceServerTimeoutRef.current) {
      window.clearTimeout(iceServerTimeoutRef.current);
    }
    setErrorMessage('');
    retryCountRef.current = 0;
    setRetryCount(0);
    cleanup();
    // Reset frontend-owned transcript state
    transcriptStore.reset();
    // Reset the DEV token readout for the next session
    tokenUsage.reset();
    setConnectionState('disconnected');
  };

  const handleManualRetry = () => {
    setErrorMessage('');
    retryCountRef.current = 0;
    setRetryCount(0);
    isReconnectingRef.current = false;
    isManualDisconnectRef.current = false;
    shouldReconnectRef.current = true;
    // Reset audio-only fallback gating so the retry attempts the avatar fresh.
    stopFallbackAudioPlayback();
    resetFallback();
    // Pass isReconnect=true when we have an existing session ID so the
    // WS URL includes session_id and the backend reuses the FieldOne session
    handleStart(savedUserIdRef.current, !!fieldOneSessionIdRef.current);
  };

  // Close WebSocket cleanly on page refresh / tab close / browser close.
  // beforeunload fires synchronously before the page is torn down, giving
  // the browser enough time to send a close frame with code 4000 so the
  // backend can finalize the session immediately instead of deferring.
  useBeforeUnload(() => {
    shouldReconnectRef.current = false;
    // Send transcript to backend before the page unloads
    sendFinalizeMessage();
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(4000, 'Page unloading');
    }
  });

  // Clean teardown on unmount.
  useEffect(() => {
    return () => {
      isManualDisconnectRef.current = true;
      shouldReconnectRef.current = false;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Browser online/offline detection for instant reconnection.
  // Without this, the browser waits ~30s for TCP timeout to detect a dead WebSocket.
  useNetworkStatus({
    onOffline: () => {
      console.log('Browser went offline');
      if (isActive && shouldReconnectRef.current) {
        setConnectionState('reconnecting');
        // Force-close the stale WebSocket so onclose fires immediately
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
        }
      }
    },
    onOnline: () => {
      console.log('Browser came back online');
      // Always attempt reconnection when network is restored (unless manually disconnected).
      // This takes priority over any stale offline reconnection cycle — clear the lock
      // and cancel any pending timeout so we start a fresh attempt immediately.
      if (shouldReconnectRef.current && !isManualDisconnectRef.current) {
        console.log('Network restored - triggering immediate reconnection');
        // Cancel any pending reconnect timeout (from exponential backoff while offline)
        if (reconnectTimeoutRef.current) {
          window.clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        // Clear the lock so attemptReconnect can proceed
        isReconnectingRef.current = false;
        attemptReconnect();
      } else {
        console.log('Network restored but reconnection not needed (manual disconnect or disabled)');
      }
    },
  });

  return {
    connectionState,
    isActive,
    retryCount,
    handleStart,
    handleStop,
    handleManualRetry,
  };
};
